import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { isAcceptedOfferCartItem, readCartItems } from '../../util/cart';

const { UUID } = sdkTypes;

const extraParams = {
  include: ['author', 'author.profileImage', 'images', 'currentStock'],
  'fields.listing': ['title', 'description', 'price', 'publicData', 'state'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
  'fields.image': [
    'variants.listing-card',
    'variants.listing-card-2x',
    'variants.square-small',
    'variants.square-small2x',
  ],
  ...createImageVariantConfig('listing-card', 400, 1),
  ...createImageVariantConfig('listing-card-2x', 800, 1),
  'limit.images': 1,
};

const searchCartListingsPayloadCreator = async (_, { dispatch, extra: sdk, rejectWithValue }) => {
  const cartItems = readCartItems();
  const ids = Array.from(new Set(cartItems.map(item => item.listingId))).slice(0, 100);
  const acceptedOfferTransactionIds = Array.from(
    new Set(
      cartItems
        .filter(isAcceptedOfferCartItem)
        .map(item => item.transactionId)
        .filter(Boolean)
    )
  ).slice(0, 100);

  if (ids.length === 0) {
    return { response: null, cartItems };
  }

  try {
    const transactionResponses = await Promise.all(
      acceptedOfferTransactionIds.map(transactionId =>
        sdk.transactions
          .show({
            id: new UUID(transactionId),
            include: ['listing', 'customer', 'provider'],
            expand: true,
          })
          .catch(() => null)
      )
    );
    transactionResponses
      .filter(Boolean)
      .forEach(response => dispatch(addMarketplaceEntities(response)));

    const response = await sdk.listings.query({
      ...extraParams,
      ids,
      page: 1,
      perPage: 100,
    });
    dispatch(addMarketplaceEntities(response));
    return { response, cartItems };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const searchCartListingsThunk = createAsyncThunk(
  'CartPage/searchCartListings',
  searchCartListingsPayloadCreator
);

export const searchCartListings = () => dispatch => {
  return dispatch(searchCartListingsThunk()).unwrap();
};

const resultIds = response => response?.data?.data?.map(l => l.id) || [];

const initialState = {
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  cartItems: [],
};

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(searchCartListingsThunk.pending, state => {
        state.searchInProgress = true;
        state.searchListingsError = null;
      })
      .addCase(searchCartListingsThunk.fulfilled, (state, action) => {
        state.searchInProgress = false;
        state.currentPageResultIds = resultIds(action.payload?.response);
        state.cartItems = action.payload?.cartItems || [];
      })
      .addCase(searchCartListingsThunk.rejected, (state, action) => {
        state.searchInProgress = false;
        state.searchListingsError = action.payload;
      });
  },
});

export default cartPageSlice.reducer;

export const loadData = () => {
  return searchCartListings();
};
